import { useEffect } from 'react';
import { useActivitySensor } from '@archipro-website/react-hooks';
import { cn } from '@rocco/utils/cn';
import type { MeUser } from '~/server/types';
import { useNotifications } from '~/modules/notifications';
import { useChat } from '../hooks/use-chat';
import { useConversations } from '../hooks/use-conversations';
import { useConversationCache } from '../hooks/use-conversation-cache';
import { useContactSuggestionsCache } from '../hooks/use-contact-suggestions';
import { useRealTimeChat } from '../hooks/use-real-time-chat';
import {
    DRAWER_MARGIN_REM,
    DRAWER_WIDTH_REM,
    playNotificationSound,
    SPACING_PX,
} from '../utils';
import { ChatInboxDrawer } from './chat-inbox/ChatInboxDrawer';
import { ChatBox } from './chat-box/ChatBox';

/**
 * The chat inbox drawer and chat boxes for the current user. This component
 * should only be rendered once in the root component. The chat inbox drawer and
 * the chat box states are managed by the `useChat` hook.
 * @see {@link useChat}
 */
export function ChatBoxes({ user }: { user: MeUser }) {
    useContactSuggestionsCache();
    const {
        chatInboxState,
        chatBoxStates,
        isInboxOrChatBoxOpen,
        setChatInboxState,
        isChatBoxActive,
        openMinimisedChatBox,
        hideInboxAndChatBoxes,
    } = useChat();
    const {
        conversations,
        isLoading,
        hasMore,
        setParams,
        resetParams,
        refetch,
        loadMore,
    } = useConversations({ limit: 20 });
    const { invalidateCachedConversation } = useConversationCache();
    const { notifications, refetch: refetchNotifications } = useNotifications();
    const { isConnected } = useRealTimeChat({
        onMessage: (message) => {
            refetch();
            refetchNotifications();
            // Invalidate cached conversation avoid showing outdated messages.
            if (!isChatBoxActive(message.conversation_id)) {
                invalidateCachedConversation(message.conversation_id);
            }
            if (
                message.type === 'chat/migrate' &&
                !isChatBoxActive(message.original_conversation_id)
            ) {
                invalidateCachedConversation(message.original_conversation_id);
            }
            // Play notification sound and open a minimised chat box if a new message is received.
            if (
                message.type === 'chat/message' ||
                message.type === 'chat/migrate'
            ) {
                if (
                    message.is_member_participant &&
                    message.sender_user_id !== user.ID &&
                    !message.is_muted
                ) {
                    openMinimisedChatBox(message.conversation_id);
                    void playNotificationSound();
                }
            }
        },
    });
    const pageActive = useActivitySensor();

    useEffect(() => {
        // Use polling to fetch the conversations list.
        let pollingInterval = 300_000; // 5 minutes
        if (!isConnected && pageActive) {
            // Fallback to polling if the WebSocket server is not connected.
            pollingInterval = 30_000; // 30 seconds
        }
        const interval = setInterval(() => {
            refetch();
        }, pollingInterval);
        return () => clearInterval(interval);
    }, [isConnected, pageActive, refetch]);

    useEffect(() => {
        // Show the chat messages button when there are new unread messages.
        if (chatInboxState === 'hidden' && notifications.userInbox?.count) {
            setChatInboxState('closed');
        }
        // Do not include chatOpenState in the dependencies to prevent
        // re-opening when the chat open state is changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications.userInbox?.count]);

    if (chatInboxState === 'hidden') {
        return null;
    }

    return (
        <>
            {isInboxOrChatBoxOpen && (
                <div
                    className={cn(
                        'fixed inset-0 z-fixed bg-black/30 duration-300 animate-in fade-in',
                        // Fix for bug in Vaul where body has "pointer-events: none"
                        // when the drawer is opened via `open` prop instead of trigger.
                        'pointer-events-auto'
                    )}
                    onClick={() => hideInboxAndChatBoxes()}
                />
            )}
            <div
                className={cn(
                    'pointer-events-none fixed bottom-4 left-0 z-fixed flex flex-row-reverse items-end justify-start',
                    // Prevent the floating button from shifting when the scrollbar is locked.
                    '[body[data-scroll-locked]_&]:translate-x-[calc(0px-var(--removed-body-scroll-bar-size,15px))]'
                )}
                style={{
                    right: `${DRAWER_WIDTH_REM + DRAWER_MARGIN_REM}rem`,
                    marginRight: `${SPACING_PX}px`,
                    gap: `${SPACING_PX}px`,
                }}
            >
                {chatBoxStates.map((chatBoxState) => (
                    <ChatBox
                        key={chatBoxState.id}
                        className="pointer-events-auto shrink-0"
                        user={user}
                        conversationID={chatBoxState.id}
                        conversation={
                            chatBoxState.id > 0
                                ? conversations?.find(
                                      (c) => c.ID === String(chatBoxState.id)
                                  )
                                : undefined
                        }
                    />
                ))}
            </div>
            <ChatInboxDrawer
                conversations={conversations}
                user={user}
                isLoading={isLoading}
                hasMore={hasMore}
                onOpenChange={() => {
                    // Reset params when opened to not keep previous filters when closed.
                    resetParams();
                }}
                onFiltersChange={({ search, projectIDs, isArchived }) => {
                    setParams({ search, buildHubIDs: projectIDs, isArchived });
                }}
                onLoadMore={loadMore}
            />
        </>
    );
}
